import { useState } from "react";
import { useT } from "../i18n";

function toISO(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function startOfMonth(year: number, month: number): Date {
  return new Date(year, month, 1);
}

const WEEKDAY_KEYS = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"] as const;

export function Calendar({
  selected,
  minISO,
  onSelect,
}: {
  selected: string;
  minISO: string;
  onSelect: (dateISO: string) => void;
}) {
  const { t, lang } = useT();
  const selectedDate = new Date(`${selected}T12:00:00`);
  const [cursor, setCursor] = useState(() => startOfMonth(selectedDate.getFullYear(), selectedDate.getMonth()));

  const year = cursor.getFullYear();
  const month = cursor.getMonth();
  const first = startOfMonth(year, month);
  const firstWeekday = first.getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const todayISO = toISO(new Date());
  const minMonth = minISO.slice(0, 7);
  const canGoPrev = `${year}-${String(month + 1).padStart(2, "0")}` > minMonth;

  const cells: Array<{ iso: string; day: number } | null> = [];
  for (let i = 0; i < firstWeekday; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) {
    cells.push({ iso: toISO(new Date(year, month, d)), day: d });
  }

  const monthLabel = new Intl.DateTimeFormat("en-US", {
    month: "long",
    year: "numeric",
  }).format(cursor);

  return (
    <div className="calendar">
      <div className="calendar-head">
        <button
          type="button"
          className="calendar-nav"
          disabled={!canGoPrev}
          onClick={() => setCursor(new Date(year, month - 1, 1))}
          aria-label={t("calendarPrevMonth")}
        >
          ‹
        </button>
        <span className="calendar-month">{monthLabel}</span>
        <button
          type="button"
          className="calendar-nav"
          onClick={() => setCursor(new Date(year, month + 1, 1))}
          aria-label={t("calendarNextMonth")}
        >
          ›
        </button>
      </div>
      <div className="calendar-grid calendar-weekdays">
        {WEEKDAY_KEYS.map((k) => (
          <span key={k}>{t(k)}</span>
        ))}
      </div>
      <div className="calendar-grid">
        {cells.map((cell, i) => {
          if (!cell) return <span key={`blank-${i}`} />;
          const disabled = cell.iso < minISO;
          const isSelected = cell.iso === selected;
          const isToday = cell.iso === todayISO;
          return (
            <button
              key={cell.iso}
              type="button"
              disabled={disabled}
              className={`calendar-day${isSelected ? " selected" : ""}${isToday ? " today" : ""}`}
              onClick={() => onSelect(cell.iso)}
            >
              {cell.day}
            </button>
          );
        })}
      </div>
    </div>
  );
}
